import { useState } from 'react';

import {
  Fieldset,
  Label,
  RadioButtonField,
  Typography,
} from '@remarkable/ark-web';

import { Password } from '@remarkable/ark-icons';
import type { FleetDevice } from 'src/api/endpoints/cloudEnterprise.types';
import type { PolicyMinimumPincodeLengthValues } from 'src/api/endpoints/cloudEnterprisePolicyApi.types';
import { useSendCommandSetPasscodeLength } from 'src/api/queries/enterprisePolicies';
import { Button, Modal, NotificationBox } from 'src/components';
import { DeviceName } from 'src/components/DeviceName';

export function DeviceActionTriggerSetPasscodeLength({
  device,
  userId,
  onSuccess,
}: {
  device: FleetDevice;
  userId: string;
  onSuccess?: () => void;
}) {
  const sendCommandSetPasscodeLength = useSendCommandSetPasscodeLength();
  const [minimumPincodeLength, setMinimumPincodeLength] =
    useState<PolicyMinimumPincodeLengthValues>(
      (device.passcodeDigits as PolicyMinimumPincodeLengthValues) ?? 0
    );
  const passcodeLengthValues = [
    0, 4, 6, 8,
  ] satisfies PolicyMinimumPincodeLengthValues[];

  return (
    <Modal.Content>
      <Modal.Title className="flex justify-center">
        <Typography
          variant="responsive-heading-md"
          className="flex items-center gap-8"
        >
          <Password size={32} className="inline" /> Passcode
        </Typography>
      </Modal.Title>
      <Modal.Description className="mt-24 flex flex-col gap-24">
        <Typography variant="body-md">
          Require the device to use a passcode and set a minimum passcode
          length.
        </Typography>

        <div className="rounded-sm bg-paper-3 p-16">
          <Typography variant="body-md">
            <b>
              <DeviceName device={device} />
            </b>{' '}
            <span className="whitespace-nowrap">({device.serialNumber})</span>
          </Typography>
        </div>
      </Modal.Description>

      <div className="flex flex-col gap-12 py-16">
        <Label>Passcode length</Label>
        <Fieldset className="grid grid-cols-2 items-center justify-center gap-12 lm:flex">
          {passcodeLengthValues.map((option) => (
            <RadioButtonField
              key={option}
              name="minimumPincodeLength"
              label={option === 0 ? 'None' : `${option} pin`}
              value={option}
              checked={minimumPincodeLength === option}
              onChange={() => {
                setMinimumPincodeLength(option);
              }}
            />
          ))}
        </Fieldset>
      </div>

      <div className="mt-24 flex justify-center gap-16">
        <Modal.Close asChild>
          <Button variant="secondary">Cancel</Button>
        </Modal.Close>

        <Button
          variant="primary"
          loading={sendCommandSetPasscodeLength.isPending}
          onClick={() => {
            sendCommandSetPasscodeLength.mutate(
              {
                deviceId: device.serialNumber,
                userId,
                length: minimumPincodeLength,
              },
              { onSuccess }
            );
          }}
        >
          Send
        </Button>
      </div>

      <NotificationBox
        error={sendCommandSetPasscodeLength.error}
        className="mt-16"
      />
    </Modal.Content>
  );
}
